import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { STORAGE_KEYS } from '../constants';
import { apiClient } from '../services/api';
import { useTimezone, formatInTimezone } from '@kioskgaming/ui';

import { resolveAdminNotificationPollMs } from '../utils/adminNotificationPoll';

const POLL_MS = resolveAdminNotificationPollMs(process.env.REACT_APP_ADMIN_NOTIFICATION_POLL_MS);
const BELL_LIMIT = 15;

export type AdminNotificationRow = {
  id: string;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'critical';
  category: string;
  actionUrl: string | null;
  aggregatedCount: number;
  isAnnouncement: boolean;
  createdAt: string;
  acknowledged: boolean;
};

function severityDotClass(severity: AdminNotificationRow['severity']): string {
  switch (severity) {
    case 'critical':
      return 'bg-destructive';
    case 'warning':
      return 'bg-amber-500';
    default:
      return 'bg-primary';
  }
}

function formatNotificationTime(iso: string | null | undefined, timezone: string): string {
  if (iso == null || iso === '') return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    return `Today, ${formatInTimezone(date, timezone, { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  }
  if (diffHours < 48) {
    return `Yesterday, ${formatInTimezone(date, timezone, { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  }
  return formatInTimezone(date, timezone, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function resolveNavigatePath(actionUrl: string | null): string | null {
  if (!actionUrl) return null;
  if (actionUrl.startsWith('/')) return actionUrl;
  try {
    const url = new URL(actionUrl);
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

export default function AdminNotificationBell() {
  const navigate = useNavigate();
  const { timezone } = useTimezone();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AdminNotificationRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const loadUnreadCount = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) {
      setUnreadCount(0);
      return;
    }
    try {
      const res = await apiClient.getAdminNotificationUnreadCount();
      if (res.success && res.data) {
        setUnreadCount(res.data.unreadCount);
      }
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const loadItems = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.getAdminNotifications(BELL_LIMIT, { unreadOnly: true });
      if (res.success && res.data?.notifications) {
        setItems(res.data.notifications);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUnreadCount();
    const t = setInterval(() => void loadUnreadCount(), POLL_MS);
    return () => clearInterval(t);
  }, [loadUnreadCount]);

  useEffect(() => {
    if (!open) return;
    void loadItems();
  }, [open, loadItems]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleItemClick = useCallback(
    async (item: AdminNotificationRow) => {
      if (!item.acknowledged) {
        try {
          await apiClient.ackAdminNotification(item.id);
          setItems((prev) =>
            prev.map((n) => (n.id === item.id ? { ...n, acknowledged: true } : n)),
          );
          setUnreadCount((c) => Math.max(0, c - 1));
        } catch {
          // noop
        }
      }
      const path = resolveNavigatePath(item.actionUrl);
      if (path) {
        setOpen(false);
        navigate(path);
      }
    },
    [navigate],
  );

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground shadow-sm transition hover:bg-muted hover:text-foreground"
        aria-expanded={open}
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" strokeWidth={2} />
        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-[min(100vw-1.5rem,22rem)] overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
          role="dialog"
          aria-label="Notification list"
        >
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <p className="text-sm font-medium">Notifications</p>
            {unreadCount > 0 ? (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                {unreadCount} new
              </span>
            ) : null}
          </div>
          <div className="max-h-[min(70vh,22rem)] overflow-y-auto">
            {loading && items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">No new notifications</p>
            ) : (
              <ul>
                {items.map((item) => {
                  const timeLabel = formatNotificationTime(item.createdAt, timezone);
                  const isRead = Boolean(item.acknowledged);
                  return (
                    <li key={item.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex w-full cursor-pointer items-start gap-2.5 border-b border-border px-4 py-3 text-left transition last:border-b-0 hover:bg-muted/50"
                        style={{ opacity: isRead ? 0.55 : 1 }}
                        onClick={() => void handleItemClick(item)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            void handleItemClick(item);
                          }
                        }}
                      >
                        <span
                          className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${severityDotClass(item.severity)}`}
                          style={{ background: isRead ? 'transparent' : undefined }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="mb-0.5 flex flex-wrap items-center gap-1.5">
                            <span className="text-sm font-medium">{item.title}</span>
                            {item.isAnnouncement ? (
                              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Announcement
                              </span>
                            ) : null}
                          </span>
                          <span className="mb-1 block whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                            {item.body}
                          </span>
                          {timeLabel ? (
                            <span className="block text-[11px] text-muted-foreground/80">{timeLabel}</span>
                          ) : null}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="border-t border-border px-4 py-2.5">
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
            >
              View all
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
